import type {
  InfiniteData,
  UseSuspenseInfiniteQueryOptions,
} from "@tanstack/react-query";
import parseLinkHeader from "parse-link-header";
import { PER_OPEN } from "../queryCachePolicy";
import type { AccessToken } from "../types";
import type { components } from "../types/voislog_api";
import { voislogApi } from "../utils/apiClients/voislogApi";

/** 通知履歴のクエリキー。ユーザー別キャッシュの無効化は先頭要素のみ指定する */
export const NOTIFICATIONS_KEY = "notifications";

/** リマインダー履歴のクエリキー。ユーザー別キャッシュの無効化は先頭要素のみ指定する */
export const REMINDER_HISTS_KEY = "reminderHists";

const PAGE_SIZE = 100;

type Page<T> = {
  items: T[];
  /** 次ページの skip。続きが無ければ null */
  nextSkip: number | null;
};

export type NotificationPage = Page<components["schemas"]["AlertResponse"]>;

export type ReminderHistPage = Page<
  components["schemas"]["ReminderHistResponse"]
>;

type HistoryQueryOptions<TPage> = UseSuspenseInfiniteQueryOptions<
  TPage,
  Error,
  InfiniteData<TPage>,
  (string | number)[],
  number
>;

// 通知履歴。新しい順に100件ずつ取り、続きの有無は Link ヘッダで判断する。
// openedAt は通知画面を開いた時刻。開くたびに別のキーにして、最初のページから読み直す
export function notificationsQueryOptions(
  tokenInfo: AccessToken | null,
  openedAt: number,
): HistoryQueryOptions<NotificationPage> {
  return {
    queryKey: [NOTIFICATIONS_KEY, tokenInfo?.access_token ?? "", openedAt],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<NotificationPage> => {
      const { data, response } = await voislogApi.GET(
        "/v2/core/notifications/history/list",
        {
          params: {
            query: {
              user_id: tokenInfo!.user_id,
              skip: pageParam,
              limit: PAGE_SIZE,
            },
          },
        },
      );
      const next = parseLinkHeader(response.headers.get("Link"))?.next;
      return {
        items: data!,
        nextSkip: next?.skip === undefined ? null : Number(next.skip),
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextSkip,
    ...PER_OPEN,
  };
}

// リマインダー履歴。通知と同じく100件ずつ取り、続きの有無は Link ヘッダで判断する。
// 通知画面を開くたびに読み直す
export function reminderHistsQueryOptions(
  tokenInfo: AccessToken | null,
  openedAt: number,
): HistoryQueryOptions<ReminderHistPage> {
  return {
    queryKey: [REMINDER_HISTS_KEY, tokenInfo?.access_token ?? "", openedAt],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<ReminderHistPage> => {
      const { data, response } = await voislogApi.GET(
        "/v2/core/reminder_hists/list",
        {
          params: {
            query: {
              user_id: tokenInfo!.user_id,
              skip: pageParam,
              limit: PAGE_SIZE,
            },
          },
        },
      );
      const next = parseLinkHeader(response.headers.get("Link"))?.next;
      return {
        items: data!,
        nextSkip: next?.skip === undefined ? null : Number(next.skip),
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextSkip,
    ...PER_OPEN,
  };
}
